<template>
  <component
    :is="as"
    ref="container"
    class="ellipsis"
    data-cy="ellipseContent"
    @mouseenter="checkShowTooltip"
  >
    <slot />
    <BaseStreemTooltip
      v-if="showTooltip"
      ref="tooltip"
      :anchor="tooltipAnchor"
      data-cy="ellipseContentTooltip"
      no-caret
      :self="tooltipSelf"
    >
      <slot
        :is-ellipsed="isEllipsed"
        name="tooltip"
      >
        <slot />
      </slot>
    </BaseStreemTooltip>
  </component>
</template>

<script setup lang="ts">
import type { QTooltip } from "quasar";
import { nextTick, onMounted, ref } from "vue";

import BaseStreemTooltip from "../BaseStreemTooltip";

export interface Props {
  as?: keyof HTMLElementTagNameMap;
  forceTooltip?: boolean;
  tooltipAnchor?: QTooltip["anchor"];
  tooltipSelf?: QTooltip["self"];
}

const props = withDefaults(defineProps<Props>(), {
  as: "div",
  tooltipAnchor: undefined,
  tooltipSelf: undefined,
});

const isEllipsed = ref(false);
const showTooltip = ref(Boolean(props.forceTooltip));
const container = ref<HTMLDivElement>();
const tooltip = ref<InstanceType<typeof BaseStreemTooltip>>();

function updateEllipsed(): void {
  isEllipsed.value =
    (container.value?.scrollWidth || 0) > (container.value?.offsetWidth || 0);
}

function checkShowTooltip(): void {
  updateEllipsed();

  showTooltip.value = props.forceTooltip || isEllipsed.value;

  // trigger tooltip manually when first shown
  if (showTooltip.value && !tooltip.value) {
    nextTick(() => {
      tooltip.value?.show();
    });
  }
}

onMounted(() => {
  updateEllipsed();
});

defineExpose({
  isEllipsed,
});
</script>
